import { useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  BiFileBlank,
  BiGridAlt,
  BiPlus,
  BiRightArrowAlt,
  BiSpreadsheet,
} from "react-icons/bi";
import {
  showLoginModal,
  showRegisterModal,
} from "../../store/auth/login/actions";
import { setLastRoute } from "../../store/routes/actions";
import { useAuth } from "../../hooks/useAuth";
import Footer from "../Footer/Footer";
import StoreButtons from "../../components/StoreButtons";
import {
  PLANS,
  PLAN_ORDER,
  formatLimit,
  formatPrice,
} from "../PricingPage/plans";
import Hero from "./Hero";
import BoothDay from "./BoothDay";
import ProductTour from "./ProductTour";
import FeatureBento from "./FeatureBento";
import "./home.css";

const eventTypes = [
  "Craft fairs",
  "Farmers markets",
  "Renaissance faires",
  "Comic cons",
  "Flea markets",
  "Night markets",
  "Music festivals",
  "Holiday bazaars",
  "Pop-up shops",
  "Art walks",
];

const freeTools = [
  {
    title: "Booth Layout Planner",
    description: "Sketch your tent, tables, and displays before load-in.",
    href: "/tools/booth-layout",
    icon: BiGridAlt,
    tag: "No account needed",
  },
  {
    title: "PDF Editor",
    description: "Fill out and sign vendor applications in your browser.",
    href: "/pdf-editor",
    icon: BiFileBlank,
    requiresLogin: true,
  },
  {
    title: "Excel Inventory Import",
    description: "Bring in your whole catalog, including Square exports.",
    href: "/import",
    icon: BiSpreadsheet,
    requiresLogin: true,
  },
];

const faqs = [
  {
    q: "Who is VendorOrbis for?",
    a: "Vendors who sell at markets, craft fairs, festivals, conventions, and pop-ups, including makers, resellers, food and merch sellers, and the teams that work with them.",
  },
  {
    q: "Does it work without internet?",
    a: "Partly. You can record cash and QR code payments with no signal: they’re saved on your phone and sync automatically when you’re back online. Square card payments need an internet connection.",
  },
  {
    q: "Can I take card payments?",
    a: "Yes. Connect your Square account to take cards with Tap to Pay or a Square reader. You can also accept cash and QR methods like Venmo, Cash App, and Zelle.",
  },
  {
    q: "Is there a free plan?",
    a: "Yes, and it doesn't expire. The Free plan includes booth checkout, Square, and event, inventory, and expense tracking for up to 10 active events and 25 items. You only pay if you need more, and Pro comes with a 30-day free trial.",
  },
  {
    q: "Where can I use it?",
    a: "Sell and manage events from the iPhone and Android app. Use the website as your back office for inventory, finance, tax reports, and tools.",
  },
];

const Homepage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    const hash = location.hash;
    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [location]);

  const handleStartTracking = () => {
    if (isAuthenticated) {
      navigate("/inventory");
    } else {
      dispatch(showRegisterModal());
    }
  };

  return (
    <div className="min-h-screen bg-white font-sans text-gray-800">
      <Hero isAuthenticated={isAuthenticated} onStart={handleStartTracking} />

      <div className="relative overflow-hidden border-y border-white/5 bg-[#0a0f1e] py-5">
        <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-24 bg-gradient-to-r from-[#0a0f1e] to-transparent" />
        <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-24 bg-gradient-to-l from-[#0a0f1e] to-transparent" />
        <div className="vo-marquee flex w-max">
          {[0, 1].map((copy) => (
            <ul
              key={copy}
              aria-hidden={copy === 1}
              className="flex shrink-0 items-center"
            >
              {eventTypes.map((type) => (
                <li
                  key={type}
                  className="flex items-center gap-8 pr-8 text-sm font-medium uppercase tracking-[0.18em] text-slate-400"
                >
                  {type}
                  <span className="h-1 w-1 rounded-full bg-blue-500" />
                </li>
              ))}
            </ul>
          ))}
        </div>
      </div>

      <BoothDay />
      <ProductTour />
      <FeatureBento />

      <section className="relative isolate overflow-hidden bg-[#070b17] px-5 py-24 text-white md:px-8 md:py-32">
        <div
          aria-hidden
          className="absolute -left-40 bottom-0 -z-10 h-[420px] w-[520px] rounded-full bg-blue-600/20 blur-[130px]"
        />
        <div className="mx-auto grid max-w-6xl items-center gap-14 lg:grid-cols-[1fr_1.2fr]">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.22em] text-blue-400">
              On the web
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight md:text-5xl">
              Phone at the booth.
              <span className="block text-slate-500">Laptop at home.</span>
            </h2>
            <p className="mt-5 max-w-lg text-lg leading-8 text-slate-400">
              The website is your back office. Edit inventory in bulk, dig into
              your finances, and run sales tax reports on a big screen, plus a
              few free tools for show prep.
            </p>
            <Link
              to="/tools"
              className="mt-8 inline-flex items-center gap-2 font-semibold text-blue-300 transition hover:text-blue-200"
            >
              Browse all tools <BiRightArrowAlt size={20} />
            </Link>
          </div>

          <div className="grid gap-4">
            {freeTools.map(({ title, description, href, icon: Icon, tag, requiresLogin }) => (
              <Link
                key={title}
                to={href}
                onClick={(event) => {
                  // Logged out: ask them to sign in here, then open the tool.
                  if (!requiresLogin || isAuthenticated) return;
                  event.preventDefault();
                  dispatch(showLoginModal());
                  dispatch(setLastRoute(href));
                }}
                className="group flex items-center gap-5 rounded-2xl border border-white/10 bg-white/[0.03] p-5 transition hover:border-white/20 hover:bg-white/[0.06]"
              >
                <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500/20 to-violet-500/20 text-blue-300">
                  <Icon size={24} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-white">{title}</span>
                    {tag && (
                      <span className="rounded-full bg-emerald-400/10 px-2 py-0.5 text-[11px] font-medium text-emerald-300">
                        {tag}
                      </span>
                    )}
                  </span>
                  <span className="mt-1 block text-sm text-slate-400">
                    {description}
                  </span>
                </span>
                <BiRightArrowAlt
                  size={22}
                  className="flex-shrink-0 text-slate-500 transition group-hover:translate-x-1 group-hover:text-white"
                />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#f6f7fb] px-5 py-24 md:px-8 md:py-32">
        <div className="mx-auto max-w-6xl">
          <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
            <div className="max-w-xl">
              <p className="text-sm font-semibold uppercase tracking-[0.22em] text-blue-600">
                Pricing
              </p>
              <h2 className="mt-3 text-3xl font-semibold tracking-tight text-slate-900 md:text-5xl">
                Free to start. Pay only when you outgrow it.
              </h2>
            </div>
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 font-semibold text-blue-600 hover:text-blue-700"
            >
              Compare all plans <BiRightArrowAlt size={20} />
            </Link>
          </div>

          <div className="mt-12 grid gap-5 md:grid-cols-3">
            {PLAN_ORDER.map((key) => {
              const plan = PLANS[key];
              const isPro = key === "pro";
              return (
                <Link
                  key={key}
                  to="/pricing"
                  className={`group relative overflow-hidden rounded-3xl p-7 transition hover:-translate-y-1 ${
                    isPro
                      ? "bg-[#070b17] text-white shadow-[0_30px_60px_-30px_rgba(79,70,229,0.6)]"
                      : "border border-slate-200 bg-white text-slate-900"
                  }`}
                >
                  {isPro && (
                    <div
                      aria-hidden
                      className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-violet-600/40 blur-[70px]"
                    />
                  )}
                  <div className="relative flex items-center justify-between">
                    <span className="text-lg font-semibold">{plan.title}</span>
                    {plan.badge && (
                      <span className="rounded-full bg-gradient-to-r from-blue-500 to-violet-500 px-2.5 py-1 text-xs font-semibold text-white">
                        {plan.badge}
                      </span>
                    )}
                  </div>
                  <p
                    className={`relative mt-1 text-sm ${
                      isPro ? "text-slate-400" : "text-slate-500"
                    }`}
                  >
                    {plan.tagline}
                  </p>
                  <p className="relative mt-6 flex items-end gap-1">
                    <span className="text-4xl font-bold tracking-tight">
                      {formatPrice(plan.monthlyPrice)}
                    </span>
                    <span
                      className={`pb-1 text-sm ${
                        isPro ? "text-slate-400" : "text-slate-500"
                      }`}
                    >
                      {plan.monthlyPrice === 0 ? "forever" : "/month"}
                    </span>
                  </p>
                  <p
                    className={`relative mt-5 border-t pt-5 text-sm ${
                      isPro
                        ? "border-white/10 text-slate-300"
                        : "border-slate-100 text-slate-600"
                    }`}
                  >
                    {formatLimit(plan.limits.events)} events ·{" "}
                    {formatLimit(plan.limits.items)} items
                    {isPro && " · employees"}
                  </p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-white px-5 py-24 md:px-8 md:py-32">
        <div className="mx-auto grid max-w-6xl gap-12 lg:grid-cols-[1fr_1.6fr]">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.22em] text-blue-600">
              FAQ
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight text-slate-900 md:text-5xl">
              Common questions
            </h2>
            <p className="mt-4 text-slate-600 md:text-lg">
              Can’t find what you need?{" "}
              <Link to="/faq" className="font-medium text-blue-600 hover:underline">
                See all answers
              </Link>
              .
            </p>
          </div>
          <div className="space-y-3">
            {faqs.map((item) => (
              <details
                key={item.q}
                className="group rounded-2xl border border-slate-200 bg-white px-5 transition open:shadow-[0_20px_45px_-25px_rgba(15,23,42,0.3)] md:px-7"
              >
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 py-5 text-left text-base font-semibold text-slate-900 md:text-lg [&::-webkit-details-marker]:hidden">
                  {item.q}
                  <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-600 transition group-open:rotate-45 group-open:bg-blue-600 group-open:text-white">
                    <BiPlus size={18} />
                  </span>
                </summary>
                <p className="pb-6 leading-7 text-slate-600">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white px-5 pb-24 md:px-8 md:pb-32">
        <div className="relative isolate mx-auto max-w-6xl overflow-hidden rounded-[32px] bg-[#070b17] px-6 py-16 text-center text-white md:px-12 md:py-24">
          <div
            aria-hidden
            className="absolute inset-0 -z-10 bg-[radial-gradient(rgba(148,163,184,0.14)_1px,transparent_1px)] [background-size:22px_22px] [mask-image:radial-gradient(ellipse_at_center,black,transparent_75%)]"
          />
          <div
            aria-hidden
            className="absolute -bottom-40 left-1/2 -z-10 h-[420px] w-[760px] -translate-x-1/2 rounded-full bg-gradient-to-r from-blue-600/40 to-violet-600/40 blur-[110px]"
          />
          <h2 className="mx-auto max-w-3xl text-3xl font-semibold tracking-tight md:text-6xl">
            Ready for your next market?
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-lg text-slate-400">
            Set up your first event in minutes. It's free, and it stays free
            until you need more.
          </p>
          <div className="mt-9 flex flex-col items-center gap-6">
            <button
              type="button"
              onClick={handleStartTracking}
              className="inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 font-semibold text-slate-900 transition hover:-translate-y-0.5 hover:bg-slate-100"
            >
              {isAuthenticated ? "Go to Dashboard" : "Start for free"}
              <BiRightArrowAlt size={20} />
            </button>
            <StoreButtons variant="glass" className="justify-center" />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Homepage;
